
import React, { useState, useEffect } from 'react';
import { Lightbulb, Search, Info, TrendingUp, AlertTriangle, ChevronDown, CheckCircle, PenTool, Trash2, Target } from 'lucide-react';
import { GoalMode, AudienceType, Environment, Insight, InsightDecision } from '../types';
import EmptyState from '../components/EmptyState';

interface InsightsProps {
  goalMode: GoalMode;
  audienceMode: AudienceType;
  activeEnv: Environment | null;
}

const Insights: React.FC<InsightsProps> = ({ goalMode, audienceMode, activeEnv }) => {
  const [decisions, setDecisions] = useState<Record<string, InsightDecision>>({});
  const [seoKeywords, setSeoKeywords] = useState<any[]>([]);
  const [loadingSEO, setLoadingSEO] = useState(false);

  const isSample = activeEnv?.isSample || false;
  const hasAnyData = activeEnv ? (Object.values(activeEnv.connections) as any).some((v: any) => v.connected) : false;
  const showData = isSample || hasAnyData;

  // Fetch SEO insights on mount
  useEffect(() => {
    if (!activeEnv || isSample) return;

    const fetchSEOInsights = async () => {
      setLoadingSEO(true);
      try {
        const response = await fetch('/api/seo/research', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'keywords',
            topic: 'laser energetics'
          })
        });

        if (response.ok) {
          const data = await response.json();
          setSeoKeywords(data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch SEO insights:', error);
      } finally {
        setLoadingSEO(false);
      }
    };

    fetchSEOInsights();
  }, [activeEnv?.id, isSample]);

  if (!activeEnv) return <EmptyState />;

  const handleDecision = (id: string, decision: InsightDecision) => {
    setDecisions(prev => ({ ...prev, [id]: decision }));
  };

  const mockInsights: Insight[] = [
    {
      id: 'ins-1',
      type: 'search_opportunity',
      title: 'Untapped Intent: "Inertial Fusion Fellowships"',
      description: 'Rising interest from STEM graduates, but the landing page is ranking on page 3 with 0.1% CTR.',
      evidence: {
        query: 'fusion fellowships 2025',
        delta: '+48% MoM',
        trend: 'rising',
        source: 'Google Search Console'
      },
      confidence_score: 0.94,
      recommended_actions: ['Create specific FAQ for International Applicants', 'Update H1 to match Fellowship query'],
      decision: 'none'
    },
    {
      id: 'ins-2',
      type: 'broken_funnel',
      title: 'The "Consideration Gap" in HED Physics',
      description: 'High traffic to news releases, but almost zero progression to laboratory facility enquiry forms.',
      evidence: {
        query: 'HED physics news',
        trend: 'flat',
        source: 'GA4 Path Exploration'
      },
      confidence_score: 0.82,
      recommended_actions: ['Add Facility Tour CTAs to all HED-related news posts'],
      decision: 'none'
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900">Content Intelligence</h2>
          <p className="text-slate-500 mt-1 text-sm italic">
            Institutional decision-support for <span className="font-bold">{goalMode}</span>.
          </p>
        </div>
      </div>

      {!showData && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-center gap-4 text-amber-800">
          <AlertTriangle size={20} />
          <p className="text-sm font-bold italic">Analytical engine hibernating. Connect data tunnels in Settings to wake up.</p>
        </div>
      )}

      <div className={`space-y-6 ${!showData ? 'opacity-30 grayscale pointer-events-none' : ''}`}>
        {/* SEO Keyword Opportunities */}
        {seoKeywords.length > 0 && (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-8 py-5 border-b border-slate-100 bg-gradient-to-r from-[#003B71] to-[#0B2341] flex items-center justify-between">
              <h3 className="font-black text-lg text-white flex items-center gap-2">
                <Target size={18} />
                SEO Content Opportunities
              </h3>
              <span className="text-xs font-bold text-white/80">{seoKeywords.length} Keywords Identified</span>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {seoKeywords.slice(0, 8).map((keyword, idx) => (
                <div key={idx} className="p-4 rounded-xl border border-slate-200 hover:border-[#FFCD00] transition-all group">
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-bold text-sm text-slate-900 group-hover:text-[#003B71] transition-colors">{keyword.keyword}</h4>
                    <span className={`text-[10px] px-2 py-1 rounded font-bold ${
                      keyword.difficulty < 30 ? 'bg-green-100 text-green-700' :
                      keyword.difficulty < 60 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      Difficulty: {keyword.difficulty}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                    <span><TrendingUp size={12} className="inline mr-1" />{keyword.searchVolume?.toLocaleString()}/mo</span>
                    <span className="uppercase text-[10px] font-bold">{keyword.intent}</span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">{keyword.recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Behavioral Insights */}
        {mockInsights.map((insight) => (
          <div key={insight.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden group hover:border-[#FFCD00] transition-all">
            <div className="p-8">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-[10px] font-black uppercase text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                      {insight.type.replace('_', ' ')}
                    </span>
                    <span className="text-[10px] font-bold text-slate-400">Confidence: {Math.round(insight.confidence_score * 100)}%</span>
                  </div>
                  <h3 className="text-2xl font-black text-slate-900 mb-3">{insight.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6 font-medium">{insight.description}</p>

                  {/* Reasoning/Explainability Block */}
                  <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100">
                    <h4 className="text-[10px] font-black uppercase text-slate-400 mb-3 flex items-center gap-2">
                      <TrendingUp size={12} /> Data Reasoning & Evidence
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Primary Metric</p>
                        <p className="text-xs font-black text-slate-900">{insight.evidence.query || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Delta</p>
                        <p className={`text-xs font-black ${insight.evidence.trend === 'rising' ? 'text-green-600' : 'text-slate-900'}`}>
                          {insight.evidence.delta || 'Inferred Pattern'}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-[9px] font-bold text-slate-400 uppercase">Source</p>
                        <p className="text-xs font-black text-slate-900">{insight.evidence.source}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-full md:w-72 border-l border-slate-100 pl-8 flex flex-col justify-between">
                  <div>
                    <h4 className="text-[10px] font-black uppercase text-slate-400 mb-4">Strategic Decision</h4>
                    <div className="space-y-2">
                      <button
                        onClick={() => handleDecision(insight.id, 'double_down')}
                        className={`w-full py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 border transition-all ${
                          decisions[insight.id] === 'double_down' ? 'bg-[#FFCD00] border-[#FFCD00] text-[#003B71]' : 'bg-slate-50 border-slate-100 text-slate-600 hover:bg-white'
                        }`}
                      >
                        <TrendingUp size={14} /> Double Down
                      </button>
                      <button
                        onClick={() => handleDecision(insight.id, 'fix_expand')}
                        className={`w-full py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 border transition-all ${
                          decisions[insight.id] === 'fix_expand' ? 'bg-[#003B71] border-[#003B71] text-white' : 'bg-slate-50 border-slate-100 text-slate-600 hover:bg-white'
                        }`}
                      >
                        <PenTool size={14} /> Fix / Expand
                      </button>
                      <button
                        onClick={() => handleDecision(insight.id, 'deprioritize')}
                        className={`w-full py-2.5 rounded-xl text-xs font-black flex items-center justify-center gap-2 border transition-all ${
                          decisions[insight.id] === 'deprioritize' ? 'bg-red-50 border-red-100 text-red-600' : 'bg-slate-50 border-slate-100 text-slate-600 hover:bg-white'
                        }`}
                      >
                        <Trash2 size={14} /> Deprioritize
                      </button>
                    </div>
                  </div>

                  {decisions[insight.id] && decisions[insight.id] !== 'none' && (
                    <div className="mt-6 p-3 bg-green-50 rounded-xl flex items-center gap-2 text-[10px] font-bold text-green-700">
                      <CheckCircle size={14} /> Decision Logged & Synced
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Insights;
